import { motion } from "motion/react";
import { cn } from "@/lib/utils";

/**
 * A casca das telas de estado — 404, falha do RootErrorBoundary e as telas
 * abertas por link de e-mail (redefinição de senha, confirmação de endereço e
 * cancelamento da exclusão da conta).
 *
 * Todas dizem a mesma coisa em situações diferentes ("você chegou numa tela de
 * passagem, e aqui está o que fazer agora"), então precisam ser o mesmo objeto:
 * mesmo card, mesma malha ao fundo, mesma hierarquia. O que muda é o `tone` —
 * azul da marca quando não há nada de errado (endereço inexistente, versão
 * nova publicada, senha a escolher) e vermelho quando algo de fato quebrou.
 *
 * Cabe formulário e cabe espera: o corpo é livre. A redefinição de senha põe
 * dois campos entre o parágrafo e os botões, e a confirmação de e-mail espera
 * o Firebase dentro do mesmo card — com o filete do `ecclesiato-loader`, não
 * com um disco girando no vazio.
 *
 * Vive fora de `ui/` de propósito: não é primitivo de shadcn, é uma tela.
 */
export type StatusTone = "brand" | "danger";

function corDoTom(tone: StatusTone) {
  return tone === "danger" ? "var(--destructive)" : "var(--primary)";
}

/** Malha fina esmaecida por máscara radial, halo na cor do tom e um filete no
 *  rodapé. Em `style` porque são gradientes sobre tokens CSS — mais legível
 *  aqui do que em quatro valores arbitrários de classe. */
function Backdrop({ tone }: { tone: StatusTone }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(to right, var(--border) 1px, transparent 1px), linear-gradient(to bottom, var(--border) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          opacity: 0.4,
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 42%, black, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 42%, black, transparent 100%)",
        }}
      />
      <div
        className="absolute top-0 left-1/2 h-144 w-xl -translate-x-1/2 -translate-y-1/2 rounded-full blur-[120px]"
        style={{
          background: `color-mix(in oklab, ${corDoTom(tone)} 14%, transparent)`,
        }}
      />
      <div
        className="absolute bottom-0 left-1/2 h-px w-full max-w-3xl -translate-x-1/2"
        style={{
          background:
            "linear-gradient(to right, transparent, var(--border), transparent)",
        }}
      />
    </div>
  );
}

export function StatusScreen({
  tone,
  role,
  children,
}: {
  tone: StatusTone;
  /** `alert` nas telas de falha; nada no 404 nem no formulário de senha, que
   *  são páginas comuns. */
  role?: "alert";
  children: React.ReactNode;
}) {
  return (
    <div
      role={role}
      className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-background p-4"
    >
      <Backdrop tone={tone} />
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.99 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-lg"
      >
        <div className="overflow-hidden rounded-2xl border border-border/60 bg-card/70 shadow-xl backdrop-blur-xl">
          {/* Filete no topo: dá o estado do card antes de qualquer texto. */}
          <div
            className="h-px w-full"
            style={{
              background: `linear-gradient(to right, transparent, color-mix(in oklab, ${corDoTom(tone)} 70%, transparent), transparent)`,
            }}
          />
          <div className="p-7 sm:p-9">{children}</div>
        </div>
      </motion.div>
    </div>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-medium tracking-[0.14em] text-muted-foreground uppercase">
      {children}
    </p>
  );
}

function IconBadge({
  tone,
  children,
}: {
  tone: StatusTone;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex size-11 shrink-0 items-center justify-center rounded-xl border shadow-xs",
        tone === "danger"
          ? "border-destructive/25 bg-destructive/10 text-destructive"
          : "border-primary/25 bg-primary/10 text-primary",
      )}
    >
      {children}
    </div>
  );
}

/**
 * Cabeçalho: badge, olho-de-boi e título empilhados, todos na mesma margem
 * esquerda. Texto centralizado em bloco pequeno lê como cartaz, e isto é uma
 * tela de sistema.
 *
 * Empilhado depois de duas tentativas lado a lado, ambas erradas por motivos
 * opostos. Com o badge no topo da coluna de texto, o centro dele caía 16,5px
 * acima do centro do título (32,5px quando o título tinha duas linhas) e o
 * ícone ficava ancorado no vão entre o olho-de-boi e o título. Centrando badge
 * e título, o alinhamento fechava, mas o olho-de-boi sobrava sozinho na linha
 * de cima com um buraco à esquerda — a composição virava um L.
 *
 * A raiz é que são três elementos de pesos muito diferentes (44px, 11px, 24px)
 * tentando conviver em duas colunas: qualquer eixo escolhido desalinha um dos
 * outros dois. Empilhando, não existe centro óptico para negociar, e a leitura
 * fica na ordem certa: o estado (cor e ícone), o rótulo, a frase.
 *
 * O ícone fica: numa tela de falha, o badge vermelho é o que se lê antes de
 * qualquer palavra. O filete no topo do card carrega o mesmo tom, mas com 1px
 * de altura ele confirma o estado — não anuncia.
 */
export function StatusHeading({
  tone,
  eyebrow,
  title,
  icon,
}: {
  tone: StatusTone;
  eyebrow: string;
  title: string;
  icon: React.ReactNode;
}) {
  return (
    <div>
      <IconBadge tone={tone}>{icon}</IconBadge>
      <div className="mt-5 space-y-1.5">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
          {title}
        </h1>
      </div>
    </div>
  );
}
